import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardBody } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { Icon } from "../../design-system/Icon";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import type { CredentialsStatus, PromptSpec } from "../../ipc/types";

interface GenImage { readonly spec: PromptSpec; status: "pending" | "done" | "error"; src: string; detail: string }

const imgSrc = (b64: string): string => (b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`);

/**
 * Generador de imágenes de publicación (Desarrollo de Producto). Claude (Anthropic) analiza la foto
 * del producto y redacta los 6 prompts optimizados para Mercado Líder; OpenAI genera las 6 imágenes.
 * La generación es secuencial (una por una) para no saturar la API y mostrar progreso.
 */
export function ProductImageStudio(): JSX.Element {
  const client = useIpc();
  const { state: creds } = useIpcQuery<CredentialsStatus>((c) => c.getCredentialsStatus(), []);
  const fileRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<{ dataUrl: string; mime: string } | null>(null);
  const [phase, setPhase] = useState<"idle" | "analyzing" | "generating">("idle");
  const [category, setCategory] = useState<string>("");
  const [images, setImages] = useState<GenImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const aiOk = creds.status === "ready" && creds.data.ai.configured;
  const imgOk = creds.status === "ready" && creds.data.images.configured;

  const onPick = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setPhoto({ dataUrl: String(reader.result), mime: file.type || "image/png" }); setImages([]); setCategory(""); setError(null); };
    reader.readAsDataURL(file);
  };

  const run = async (): Promise<void> => {
    if (!photo) return;
    setError(null); setImages([]); setCategory(""); setPhase("analyzing");
    // 1) Claude analiza la foto y escribe los 6 prompts.
    const a = await client.analyzeProductImage(photo.dataUrl, photo.mime);
    if (!a.ok) { setPhase("idle"); setError(a.error.message); return; }
    if (!a.value.ok) { setPhase("idle"); setError(a.value.detail); return; }
    setCategory(a.value.category);
    const specs = a.value.images;
    const initial: GenImage[] = specs.map((spec) => ({ spec, status: "pending", src: "", detail: "" }));
    setImages(initial);
    // 2) OpenAI genera cada imagen, en orden.
    setPhase("generating");
    setProgress({ done: 0, total: specs.length });
    for (let i = 0; i < specs.length; i++) {
      const g = await client.generateProductImage(specs[i]!.prompt);
      setImages((prev) => prev.map((im, idx) => idx !== i ? im : (
        g.ok && g.value.ok ? { ...im, status: "done", src: g.value.imageBase64, detail: g.value.detail }
        : { ...im, status: "error", detail: g.ok ? g.value.detail : g.error.message }
      )));
      setProgress({ done: i + 1, total: specs.length });
    }
    setPhase("idle"); setProgress(null);
  };

  const download = async (im: GenImage): Promise<void> => {
    if (!im.src) return;
    await client.saveImage(`publicacion-imagen-${im.spec.n}.png`, im.src);
  };

  const busy = phase !== "idle";

  return (
    <Card style={{ marginTop: "var(--sp-5)" }}>
      <CardHeader
        title={<span className="row" style={{ gap: 10 }}><Icon name="spark" size={18} /> Imágenes de la publicación (IA)</span>}
        subtitle="Sube la foto de tu producto: Claude escribe los 6 prompts óptimos para Mercado Líder y OpenAI genera las 6 imágenes."
      />
      <CardBody>
        {creds.status === "ready" && (!aiOk || !imgOk) ? (
          <div className="callout warn" style={{ marginBottom: "var(--sp-4)" }}>
            <div className="bar" />
            <div className="body">
              {!aiOk ? <>Falta tu <b className="strong">API de IA (Anthropic)</b> para el análisis. </> : null}
              {!imgOk ? <>Falta tu <b className="strong">API de imágenes (OpenAI)</b> para generar. </> : null}
              Configúralas en <Link to="/seguridad">Seguridad → Credenciales</Link>. Puedes subir la foto igual y probar el flujo.
            </div>
          </div>
        ) : null}

        <div className="row" style={{ gap: "var(--sp-4)", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 180 }}>
            <div style={{ width: 180, height: 180, border: "1px dashed var(--border)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "var(--bg-muted, rgba(127,127,127,0.06))" }}>
              {photo ? <img src={photo.dataUrl} alt="Foto del producto" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /> : <span className="faint" style={{ fontSize: 12.5, textAlign: "center", padding: 8 }}>Sin foto</span>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} aria-label="Subir foto del producto" onChange={onPick} />
            <Button variant="secondary" size="sm" icon="product" style={{ marginTop: 8, width: "100%" }} disabled={busy} onClick={() => fileRef.current?.click()}>
              {photo ? "Cambiar foto" : "Subir foto"}
            </Button>
          </div>

          <div style={{ flex: 1, minWidth: 240 }}>
            <p className="muted" style={{ marginTop: 0, fontSize: 13.5 }}>
              Se generan 6 imágenes en orden: <b className="strong">Portada</b>, Ángulo/detalle, Infografía, Contexto de uso, Escala/contenido y Confianza/garantía.
              La IA mantiene el color y la forma reales de tu producto y respeta las políticas de Mercado Libre.
            </p>
            <div className="pd-actions">
              <Button icon="spark" disabled={!photo || busy} onClick={run}>
                {phase === "analyzing" ? "Analizando foto…" : phase === "generating" ? `Generando ${progress?.done ?? 0}/${progress?.total ?? 6}…` : "Analizar y generar 6 imágenes"}
              </Button>
              {category ? <Badge tone="success" dot>Categoría: {category}</Badge> : null}
            </div>
            {error ? (
              <div className="pd-sim__result" style={{ marginTop: "var(--sp-3)" }}>
                <Badge tone="danger" dot>Error</Badge><span className="muted" style={{ fontSize: 13.5 }}>{error}</span>
              </div>
            ) : null}
          </div>
        </div>

        {images.length > 0 ? (
          <div className="pd-cols" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", marginTop: "var(--sp-5)" }}>
            {images.map((im) => (
              <Card key={im.spec.n}>
                <CardHeader title={<span style={{ fontSize: 14 }}>{im.spec.n}. {im.spec.title}</span>} subtitle={im.spec.purpose} />
                <CardBody>
                  <div style={{ width: "100%", aspectRatio: "1 / 1", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-muted, rgba(127,127,127,0.06))" }}>
                    {im.status === "done" && im.src ? <img src={imgSrc(im.src)} alt={im.spec.title} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                     : im.status === "error" ? <span className="faint" style={{ fontSize: 12, padding: 10, textAlign: "center" }}>No se pudo generar: {im.detail}</span>
                     : <span className="faint" style={{ fontSize: 12 }}>En cola…</span>}
                  </div>
                  <details style={{ marginTop: 8 }}>
                    <summary className="muted" style={{ cursor: "pointer", fontSize: 12.5 }}>Ver prompt</summary>
                    <p className="faint" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>{im.spec.prompt}</p>
                  </details>
                  {im.status === "done" ? <Button variant="secondary" size="sm" icon="check" style={{ marginTop: 8, width: "100%" }} onClick={() => download(im)}>Descargar</Button> : null}
                </CardBody>
              </Card>
            ))}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
